import { useEffect, useRef, useState } from "react";
import type { ActorRefFrom, SnapshotFrom } from "xstate";
import { ModalHeader, ModalBody, ModalFooter } from "@design-lab/shared/ui/modal";
import { Button } from "@design-lab/shared/ui";
import { Textarea } from "@design-lab/shared/ui/textarea";
import { Input } from "components/jiffy/design-tag/components/Input";
import type { artworkMachine } from "@design-lab/entities/artwork/model/artwork.machine";

type ArtworkFeedbackFormModalContentProps = {
  artworkState: SnapshotFrom<typeof artworkMachine>;
  sendArtwork: ActorRefFrom<typeof artworkMachine>["send"];
};

const OPTIONS = [
  {
    id: "artwork-feedback-option-background-removal",
    inputId: "artwork-feedback-option-background-removal-radio",
    label: "Background Removal"
  },
  {
    id: "artwork-feedback-option-image-placement",
    inputId: "artwork-feedback-option-image-placement-radio",
    label: "Image Placement"
  },
  {
    id: "artwork-feedback-option-image-blurriness",
    inputId: "artwork-feedback-option-image-blurriness-radio",
    label: "Image Blurriness"
  },
  {
    id: "artwork-feedback-option-other-feedback",
    inputId: "artwork-feedback-option-other-feedback-radio",
    label: "Other Feedback"
  }
] as const;

export function ArtworkFeedbackFormModalContent({
  artworkState,
  sendArtwork
}: ArtworkFeedbackFormModalContentProps) {
  const [selectedOption, setSelectedOption] = useState<string | undefined>(undefined);
  const [feedbackTextArea, setFeedbackTextArea] = useState("");
  const otherFeedbackTextarea = useRef<HTMLTextAreaElement | null>(null);

  const isOtherSelected = selectedOption === "Other Feedback";
  const isSubmitDisabled = isOtherSelected ? feedbackTextArea.trim().length < 3 : !selectedOption;

  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    sendArtwork({ type: "feedback.submit" });
  };

  const handleRadioChange = (label: string) => {
    setSelectedOption(label);
    const isOther = label === "Other Feedback";
    if (isOther) {
      setFeedbackTextArea("");
      sendArtwork({ type: "feedback.select_reason", reason: "" });
    } else {
      sendArtwork({ type: "feedback.select_reason", reason: label });
    }
  };

  useEffect(() => {
    if (isOtherSelected) otherFeedbackTextarea.current?.focus();
  }, [isOtherSelected]);

  useEffect(() => {
    const reason = artworkState.context.feedbackSelectedReason;
    if (reason === undefined) return;

    if (OPTIONS.some(o => o.label === reason)) {
      setSelectedOption(reason);
      setFeedbackTextArea("");
    } else {
      setSelectedOption("Other Feedback");
      setFeedbackTextArea(reason);
    }
  }, [artworkState.context.feedbackSelectedReason]);

  return (
    <>
      <ModalHeader
        className="justify-start p-4 text-left"
        onClose={() => sendArtwork({ type: "feedback.close" })}
      >
        <div className="text-lg font-bold">Let&apos;s make it right!</div>
        <div className="text-sm font-normal text-gray-700">
          Tell us what didn&apos;t go as expected so we can fix it.
        </div>
      </ModalHeader>

      <ModalBody>
        <fieldset aria-labelledby="artwork-feedback-legend">
          <legend id="artwork-feedback-legend" className="mb-3 text-sm font-bold text-gray-700">
            Please choose primary issue:
          </legend>
          <div className="space-y-3">
            <div className="flex flex-col gap-4 py-px">
              {OPTIONS.map(({ id, inputId, label }) => (
                <Input
                  key={id}
                  id={inputId}
                  label={label}
                  name="artwork-feedback"
                  data-testid={`${inputId}-input`}
                  type="radio"
                  checked={selectedOption === label}
                  className="flex items-center gap-4"
                  labelClassName="order-2 text-sm text-gray-700"
                  inputClassName="size-4"
                  onChange={() => handleRadioChange(label)}
                />
              ))}
              <Textarea
                rows={4}
                id="artwork-message-feedback-textarea"
                name="artwork-message-feedback"
                data-testid="artwork-message-feedback-textarea"
                value={feedbackTextArea}
                ref={otherFeedbackTextarea}
                disabled={!isOtherSelected}
                placeholder="Color Accuracy / Image Cropping / Size etc..."
                onChange={({ target }) => {
                  const { value } = target as HTMLTextAreaElement;
                  setFeedbackTextArea(value);
                  if (isOtherSelected) {
                    sendArtwork({ type: "feedback.select_reason", reason: value });
                  }
                }}
              />
            </div>
          </div>
        </fieldset>
      </ModalBody>

      <ModalFooter>
        <div className="flex justify-end gap-2">
          <Button
            data-testid="artwork-feedback-cancel"
            variant="secondary"
            onPress={() => sendArtwork({ type: "feedback.close" })}
          >
            Cancel
          </Button>
          <Button
            data-testid="artwork-feedback-submit"
            onPress={handleSubmit}
            isDisabled={isSubmitDisabled}
          >
            Send Feedback
          </Button>
        </div>
      </ModalFooter>
    </>
  );
}
